/**
 * 習慣カードコンポーネント
 *
 * 習慣一覧ページで使用する個々の習慣を表示するカード。
 * チェックボックス、習慣名、アクションボタン（編集・削除）を含む。
 *
 * 機能:
 * - ワンタップでの完了/未完了切り替え
 * - 編集モーダルの呼び出し
 * - 削除確認ダイアログ
 * - モバイル/デスクトップ対応のアクションメニュー
 *
 * Rails で言えば:
 * - app/views/habits/_card.html.erb に相当
 *
 * @example
 * <HabitCard
 *   habit={habit}
 *   checked={habit.completedToday}
 *   pending={isPending}
 *   onToggle={() => handleToggle(habit.id)}
 *   onEdit={() => openEditModal(habit)}
 * />
 */

import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { router } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { HabitCheckbox } from './HabitCheckbox'
import type { Habit } from '@/types'
import * as Routes from '@/lib/routes'

interface HabitCardProps {
  /** 表示する習慣データ */
  habit: Habit
  /** 完了状態 */
  checked: boolean
  /** ローディング中 */
  pending?: boolean
  /** 予定外完了（追加実行）を示す */
  extra?: boolean
  /** 完了/未完了切り替え時のコールバック */
  onToggle: () => void
  /** 編集ボタン押下時のコールバック */
  onEdit: () => void
}

export function HabitCard({
  habit,
  checked,
  pending = false,
  extra = false,
  onToggle,
  onEdit,
}: HabitCardProps) {
  const [showActions, setShowActions] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number
    right: number
  } | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    if (!showActions) return

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowActions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showActions])

  const handleDelete = () => {
    router.delete(Routes.habit_path(habit.id), {
      preserveScroll: true,
    })
  }

  const handleToggleActions = () => {
    if (!showActions && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const position = {
        top: rect.bottom + 4, // 4px margin
        right: window.innerWidth - rect.right,
      }
      setDropdownPosition(position)
      setShowActions(true)
    } else {
      setShowActions(false)
    }
  }

  return (
    <div
      className={cn(
        'habit-card group relative',
        !habit.scheduledToday && 'opacity-60'
      )}
      style={{
        borderLeftWidth: '4px',
        borderLeftColor: habit.color || '#3b82f6',
      }}
    >
      <HabitCheckbox
        checked={checked}
        pending={pending}
        extra={extra}
        onClick={onToggle}
        color={habit.color ?? undefined}
      />

      <div className="min-w-0 flex-1">
        <h3
          className={cn(
            'text-foreground truncate font-medium',
            checked && 'text-muted-foreground line-through'
          )}
        >
          {habit.name}
        </h3>
        {habit.description && (
          <p className="text-muted-foreground mt-0.5 truncate text-sm">
            {habit.description}
          </p>
        )}
      </div>

      {/* Desktop actions */}
      <div className="hidden items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 sm:flex">
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          className="h-9 w-9"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowDeleteConfirm(true)}
          className="text-destructive hover:text-destructive h-9 w-9"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Mobile actions toggle */}
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        onClick={handleToggleActions}
        className="h-9 w-9 sm:hidden"
      >
        <MoreHorizontal className="h-5 w-5" />
      </Button>

      {/* Mobile actions dropdown */}
      {showActions &&
        createPortal(
          <div
            ref={dropdownRef}
            className="bg-card border-border fixed z-50 rounded-lg border shadow-lg sm:hidden"
            style={
              dropdownPosition
                ? {
                    top: `${dropdownPosition.top}px`,
                    right: `${dropdownPosition.right}px`,
                  }
                : {}
            }
          >
            <button
              onClick={() => {
                setShowActions(false)
                onEdit()
              }}
              className="hover:bg-muted flex w-full items-center gap-2 px-4 py-3 text-left"
            >
              <Pencil className="h-4 w-4" />
              編集
            </button>
            <button
              onClick={() => {
                setShowActions(false)
                setShowDeleteConfirm(true)
              }}
              className="text-destructive hover:bg-muted flex w-full items-center gap-2 px-4 py-3 text-left"
            >
              <Trash2 className="h-4 w-4" />
              削除
            </button>
          </div>,
          document.body
        )}

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="習慣を削除"
        description="この習慣を削除しますか？関連する記録もすべて削除されます。"
        confirmText="削除する"
        cancelText="キャンセル"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  )
}
